import { Box, Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import React, { ReactNode } from 'react';
import { FeedbackSnackbarContext } from '../../context/FeedbackSnackbarContext';
import { Moment } from 'moment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { hasBannedCharacters } from '../../lib/validation/text_validation';
import { MetadataError } from '@ep/error-handling';
import { log } from '../../util/log';
import { generateUuidV4 } from '../../util/uuid';
import GhostButton from '../shared/button/GhostButton';
import PrimaryButton from '../shared/button/PrimaryButton';
import { CaptionText, Subheading } from '../shared/typography/PrimaryTypography';

// TODO: Can ConfigAddWidget and ConfigEditWidget be combined.

type InputValueByType = {
  text: string;
  select: string | number;
  date: Moment;
  month_year: Moment;
  node: unknown;
  number: number;
  boolean: boolean;
};

export interface CreateInput<T extends keyof InputValueByType = keyof InputValueByType> {
  id: string;
  type: 'text' | 'select' | 'date' | 'month_year' | 'node' | 'number' | 'boolean';
  label: string;
  defaultValue?: string | number | Moment | boolean;
  optional?: boolean;
  hideInput?: boolean;
  ignoreBannedCharacters?: boolean;
  helperText?: string;
  inputNodeFactory?: (handleInputChange: (input_id: string, value: unknown) => void) => ReactNode;
  textValidation?: (input_value: InputValueByType[T]) => boolean;
  onChange?: (value: unknown, current_map: InputValuesMap) => InputValuesMap;
}

export type InputValuesMap = Record<string, unknown>;

interface ConfigAddWidgetProps {
  widget_label: string;
  icon: ReactNode;
  inputs: readonly CreateInput[];
  existing_entity_labels?: Array<string>;
  grid_spacing?: number;
  handleClose?: () => void;
  createFunction: ((...args: any) => void) | null;
  inputValuesMapOverride?: InputValuesMap;
  setInputValuesMapOverride?: (map: InputValuesMap) => void;
}

/**
 * Generic widget for filling in dynamic inputs required for adding an entity to the database.
 * @param {string} widget_label Label for the type of entity being created.
 * @param {ReactNode} icon Icon to display next to title.
 * @param {Array<CreateInput>} inputs List of input fields to fill in for the create function to use.
 * @param {Array<string>} existing_entity_labels Optional list of existing entity identifiers to show in relation to the input fields to avoid duplicates.
 * @param {number} grid_spacing Optional spacing to add between each input field component.
 * @param {void} handleClose Close the widget.
 * @param {void} createFunction Use the input field data to create the entity in the database.
 * @returns {JSX.Element}
 */
const ConfigAddWidget = ({
  widget_label,
  icon,
  inputs,
  existing_entity_labels,
  grid_spacing,
  handleClose,
  createFunction,
  inputValuesMapOverride,
  setInputValuesMapOverride
}: ConfigAddWidgetProps) => {
  // Default form state for inputs.
  const initial_input_values: InputValuesMap = Object.fromEntries(
    inputs.map((input: CreateInput) => [input.id, input.defaultValue ?? null])
  );

  // Internal state fallback.
  const [internalInputValuesMap, setInternalInputValuesMap] =
    React.useState<InputValuesMap>(initial_input_values);

  // Use override if provided, else fall back to internal state.
  const inputValuesMap: InputValuesMap = inputValuesMapOverride ?? internalInputValuesMap;
  const setInputValuesMap = setInputValuesMapOverride ?? setInternalInputValuesMap;

  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [createLoading, setCreateLoading] = React.useState<boolean>(false);

  // Checks if every non-optional input has a value (at least 1 option for array inputs), and no banned characters for text inputs.
  const hasValidInputs = (): boolean => {
    const inputs_valid: boolean = inputs.every((input: CreateInput) => {
      const input_value = inputValuesMap[input.id];

      const is_required_valid: boolean =
        input.optional === true ||
        (input_value !== undefined &&
          input_value !== null &&
          input_value !== '' &&
          (!Array.isArray(input_value) || input_value.length > 0));

      const has_no_banned_characters: boolean =
        input.ignoreBannedCharacters ||
        typeof input_value !== 'string' ||
        !hasBannedCharacters(input_value);

      const passes_custom_validation: boolean =
        typeof input.textValidation !== 'function' || input.textValidation(input_value);

      return is_required_valid && has_no_banned_characters && passes_custom_validation;
    });

    return inputs_valid;
  };

  const handleInputChange = (input_id: string, new_value: unknown): void => {
    const input = inputs.find((inp) => inp.id === input_id);
    if (!input) return;

    let new_map: InputValuesMap = { ...inputValuesMap, [input_id]: new_value };

    // If input has a custom change handler, call it.
    if (input.onChange) {
      new_map = input.onChange(new_value, new_map);
    }

    setInputValuesMap(new_map);
  };

  const resetDefaultValues = (): void => {
    setInputValuesMap(initial_input_values);
  };

  const handleCreateClick = async (): Promise<void> => {
    setCreateLoading(true);
    try {
      if (createFunction) {
        // Call create function with variable arguments, NOTE: supplied inputs array must be in same order of expected create function arguments.
        const argument_values: Array<unknown> = inputs.map(
          (input: CreateInput) => inputValuesMap[input.id]
        );
        await createFunction(...argument_values);
      }
      handleClose && handleClose();
      resetDefaultValues();
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'invalid create input') {
        return;
      }
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ConfigAddWidget failed on an unknown error while calling handleCreateClick.',
          {
            inputs: inputs
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create entities. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setCreateLoading(false);
    }
    return;
  };
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={grid_spacing ? grid_spacing : 6}>
          {widget_label && (
            <Grid size={10}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap'
                }}
              >
                {icon}
                <Subheading weight="Medium" sx={{ mx: 2 }}>
                  Add {widget_label}
                </Subheading>
              </Box>
            </Grid>
          )}
          {existing_entity_labels && existing_entity_labels.length > 0 && (
            <>
              <Grid size={12}>
                <Subheading>Already Existing:</Subheading>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {existing_entity_labels.map((label, index) => (
                    <CaptionText
                      dark
                      key={index}
                      sx={{
                        backgroundColor: '#E7E8E9',
                        borderRadius: 3,
                        px: 1.3,
                        py: 0.2,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {label}
                    </CaptionText>
                  ))}
                </Box>
              </Grid>
            </>
          )}
          {inputs
            .filter((input: CreateInput) => !input.hideInput)
            .map((input: CreateInput) => {
              const input_value: unknown = inputValuesMap[input.id];
              return (
                <Grid size={12} key={input.id}>
                  {input.type === 'text' && (
                    <TextField
                      error={
                        !input.ignoreBannedCharacters &&
                        typeof input_value === 'string' &&
                        input_value !== '' &&
                        hasBannedCharacters(input_value)
                      }
                      fullWidth
                      id={input.id}
                      label={input.label}
                      value={input_value ? input_value : ''}
                      variant="standard"
                      helperText={input.helperText}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        // TODO: Change to handleInputChange (and retest).
                        setInputValuesMap({ ...inputValuesMap, [input.id]: event.target.value });
                      }}
                    />
                  )}
                  {input.type === 'number' && (
                    <TextField
                      type="number"
                      fullWidth
                      id={input.id}
                      label={input.label}
                      value={input_value ? input_value : ''}
                      variant="standard"
                      helperText={input.helperText}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                        // TODO: Change to handleInputChange (and retest).
                        setInputValuesMap({ ...inputValuesMap, [input.id]: event.target.value });
                      }}
                    />
                  )}
                  {input.type === 'date' && (
                    <DatePicker
                      label={input.label}
                      value={input_value}
                      onChange={(new_date) =>
                        // TODO: Change to handleInputChange (and retest).
                        setInputValuesMap({ ...inputValuesMap, [input.id]: new_date })
                      }
                      sx={{ width: '100%' }}
                    />
                  )}
                  {input.type === 'month_year' && (
                    <DatePicker
                      views={['year', 'month']}
                      label={input.label}
                      value={input_value}
                      onChange={(new_date) => handleInputChange(input.id, new_date)}
                      sx={{ width: '100%' }}
                    />
                  )}
                  {input.type === 'boolean' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          value={input_value ? true : false}
                          color="primary"
                          onChange={(event) => {
                            // TODO: Change to handleInputChange (and retest).
                            setInputValuesMap({
                              ...inputValuesMap,
                              [input.id]: event.target.checked
                            });
                          }}
                        />
                      }
                      label={input.label}
                    />
                  )}
                  {input.type === 'node' &&
                    input.inputNodeFactory &&
                    input.inputNodeFactory(handleInputChange)}
                </Grid>
              );
            })}

          <Grid size={12} alignItems={'right'}>
            <Box m={1} display="flex" justifyContent="flex-end" alignItems="flex-end">
              <GhostButton
                dark
                startIcon={<ChevronLeftIcon />}
                onClick={() => {
                  handleClose && handleClose();
                }}
              >
                Back
              </GhostButton>
              <PrimaryButton
                loading={createLoading}
                onClick={async () => {
                  await handleCreateClick();
                }}
                disabled={!hasValidInputs()}
              >
                Create
              </PrimaryButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ConfigAddWidget;
